<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Watchlist</h5>
        <p class="page-sub">Block or flag individuals from checking in — checked automatically on every invite and arrival</p>
      </div>
      <div style="display:flex;gap:8px;">
        <button class="btn-ghost" @click="screenModal = true"><font-awesome-icon icon="search" /> Screen Visitor</button>
        <button class="btn-primary-action" @click="openCreate"><font-awesome-icon icon="plus" /> Add Entry</button>
      </div>
    </div>

    <!-- Search bar -->
    <div class="filter-bar">
      <input v-model="search" class="filter-input" placeholder="Search name, email…" @input="debounceLoad" />
      <select v-model="filterActive" class="filter-select" @change="load()">
        <option value="">All</option>
        <option value="1">Active only</option>
        <option value="0">Inactive only</option>
      </select>
    </div>

    <div v-if="loading" class="sk-table">
      <div v-for="n in 5" :key="n" class="sk-row"></div>
    </div>

    <div v-else-if="!entries.data?.length && !entries.length" class="empty-state">
      <font-awesome-icon icon="ban" class="empty-icon" />
      <p class="empty-title">Watchlist is empty</p>
      <p class="empty-sub">Add entries to automatically block or flag matching visitors during check-in.</p>
    </div>

    <div v-else class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th>Name</th><th>Email</th><th>Phone</th><th>Reason</th><th>Match %</th><th>Status</th><th>Added</th><th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="e in (entries.data ?? entries)" :key="e.id" :class="{ 'row--inactive': !e.active }">
            <td class="td-bold">{{ e.name }}</td>
            <td class="td-muted">{{ e.email ?? '—' }}</td>
            <td class="td-muted">{{ e.phone ?? '—' }}</td>
            <td>
              <span v-if="e.reason" class="reason-text">{{ e.reason }}</span>
              <span v-else class="td-muted">—</span>
            </td>
            <td><span class="threshold-badge">≥{{ e.match_threshold }}%</span></td>
            <td>
              <span class="status-dot" :class="e.active ? 'status-dot--green' : 'status-dot--red'"></span>
              {{ e.active ? 'Active' : 'Inactive' }}
            </td>
            <td class="td-muted">{{ formatDate(e.created_at) }}</td>
            <td>
              <div style="display:flex;gap:6px;justify-content:flex-end;">
                <button class="btn-icon" @click="openEdit(e)"><font-awesome-icon icon="pencil-alt" /></button>
                <button class="btn-icon btn-icon--red" @click="confirmDelete(e)"><font-awesome-icon icon="trash" /></button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <!-- Pagination -->
      <div v-if="entries.last_page > 1" class="pagination-bar">
        <button :disabled="page === 1" class="pg-btn" @click="load(page - 1)">‹ Prev</button>
        <span class="pg-info">Page {{ entries.current_page }} of {{ entries.last_page }}</span>
        <button :disabled="page === entries.last_page" class="pg-btn" @click="load(page + 1)">Next ›</button>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="modal" class="modal-backdrop" @mousedown.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <h6 class="modal-title">{{ editing ? 'Edit Entry' : 'Add Watchlist Entry' }}</h6>
          <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Full Name <span class="req">*</span></label>
            <input v-model="form.name" class="form-input" maxlength="200" placeholder="Full name of person to watch" />
          </div>
          <div class="two-col" style="margin-top:14px;">
            <div class="form-row">
              <label class="form-label">Email</label>
              <input v-model="form.email" type="email" class="form-input" placeholder="email@example.com" />
            </div>
            <div class="form-row">
              <label class="form-label">Phone</label>
              <input v-model="form.phone" class="form-input" maxlength="30" placeholder="+44…" />
            </div>
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Reason</label>
            <textarea v-model="form.reason" class="form-input" rows="2" style="resize:vertical;" placeholder="Why this person is on the watchlist"></textarea>
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Name Match Threshold ({{ form.match_threshold }}%)</label>
            <input v-model.number="form.match_threshold" type="range" min="50" max="100" step="5" class="range-input" />
            <span class="form-hint">Match scores above this threshold trigger a flag. Lower = more sensitive. 80% recommended.</span>
          </div>
          <div class="toggle-row" style="margin-top:14px;">
            <span class="toggle-name">Active</span>
            <label class="toggle-switch"><input type="checkbox" v-model="form.active" /><span class="toggle-track"></span></label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="closeModal">Cancel</button>
          <button class="btn-primary-action" @click="save" :disabled="saving">
            <font-awesome-icon v-if="saving" icon="spinner" spin />
            {{ saving ? 'Saving…' : (editing ? 'Save Changes' : 'Add to Watchlist') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Screen Visitor Modal -->
    <div v-if="screenModal" class="modal-backdrop" @mousedown.self="screenModal = false; screenResult = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6 class="modal-title">Screen a Visitor</h6>
          <button class="modal-close" @click="screenModal = false; screenResult = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;margin:0 0 16px;">Check a visitor's name and email against the watchlist without starting a visit.</p>
          <div class="two-col">
            <div class="form-row">
              <label class="form-label">First Name <span class="req">*</span></label>
              <input v-model="screenForm.first_name" class="form-input" />
            </div>
            <div class="form-row">
              <label class="form-label">Last Name <span class="req">*</span></label>
              <input v-model="screenForm.last_name" class="form-input" />
            </div>
          </div>
          <div class="form-row" style="margin-top:14px;">
            <label class="form-label">Email</label>
            <input v-model="screenForm.email" type="email" class="form-input" />
          </div>

          <div v-if="screenResult" class="screen-result" :class="screenResult.matched ? 'screen-result--match' : 'screen-result--clear'">
            <font-awesome-icon :icon="screenResult.matched ? 'exclamation-triangle' : 'check-circle'" class="screen-result-icon" />
            <div>
              <div class="screen-result-title">{{ screenResult.matched ? 'Watchlist Match Detected' : 'No Match Found' }}</div>
              <div class="screen-result-sub">Match score: {{ screenResult.match_score }}%</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="screenModal = false; screenResult = null">Close</button>
          <button class="btn-primary-action" @click="doScreen" :disabled="screening">
            <font-awesome-icon v-if="screening" icon="spinner" spin />
            {{ screening ? 'Screening…' : 'Run Screen' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirm -->
    <div v-if="deleteTarget" class="modal-backdrop" @mousedown.self="deleteTarget = null">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <h6 class="modal-title">Remove Watchlist Entry?</h6>
          <button class="modal-close" @click="deleteTarget = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;">Remove <strong>{{ deleteTarget.name }}</strong> from the watchlist? Historical screening results will remain.</p>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="btn-danger" @click="doDelete" :disabled="deleting">
            <font-awesome-icon v-if="deleting" icon="spinner" spin />Remove
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'VisitorWatchlist',
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: true, entries: [], search: '', filterActive: '', page: 1,
      modal: false, editing: null, saving: false,
      form: this.blankForm(),
      deleteTarget: null, deleting: false,
      screenModal: false, screening: false, screenResult: null,
      screenForm: { first_name: '', last_name: '', email: '' },
      debounceTimer: null,
    }
  },
  created() { this.load() },
  methods: {
    blankForm() { return { name: '', email: '', phone: '', reason: '', match_threshold: 80, active: true } },
    debounceLoad() { clearTimeout(this.debounceTimer); this.debounceTimer = setTimeout(() => this.load(1), 350) },
    async load(p = 1) {
      this.loading = true; this.page = p
      const params = { page: p }
      if (this.search) params.search = this.search
      if (this.filterActive !== '') params.active = this.filterActive
      try {
        const r = await axios.get('visitors/watchlist', { params })
        this.entries = r.data.data ?? r.data
      } catch { this.toast.error('Failed to load watchlist.') }
      finally { this.loading = false }
    },
    openCreate() { this.form = this.blankForm(); this.editing = null; this.modal = true },
    openEdit(e) { this.editing = e; this.form = { name: e.name, email: e.email ?? '', phone: e.phone ?? '', reason: e.reason ?? '', match_threshold: e.match_threshold ?? 80, active: e.active }; this.modal = true },
    closeModal() { this.modal = false; this.editing = null },
    async save() {
      if (!this.form.name.trim()) { this.toast.error('Name is required.'); return }
      this.saving = true
      try {
        if (this.editing) {
          await axios.put(`visitors/watchlist/${this.editing.id}`, this.form)
          this.toast.success('Entry updated.')
        } else {
          await axios.post('visitors/watchlist', this.form)
          this.toast.success('Added to watchlist.')
        }
        this.closeModal(); await this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to save.') }
      finally { this.saving = false }
    },
    confirmDelete(e) { this.deleteTarget = e },
    async doDelete() {
      this.deleting = true
      try {
        await axios.delete(`visitors/watchlist/${this.deleteTarget.id}`)
        this.toast.success('Entry removed.'); this.deleteTarget = null; await this.load()
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Failed to remove.') }
      finally { this.deleting = false }
    },
    async doScreen() {
      if (!this.screenForm.first_name || !this.screenForm.last_name) { this.toast.error('First and last name required.'); return }
      this.screening = true; this.screenResult = null
      try {
        const r = await axios.post('visitors/watchlist/screen', this.screenForm)
        this.screenResult = r.data.data ?? r.data
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Screening failed.') }
      finally { this.screening = false }
    },
    formatDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—' },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost   { display:inline-flex; align-items:center; gap:6px; height:38px; padding:0 16px; border:1px solid #dde5f0; border-radius:8px; background:#fff; font-size:13px; color:#3d5068; cursor:pointer; font-family:inherit; }
.btn-ghost:hover { background:#f4f7fb; }
.btn-icon    { display:inline-flex; align-items:center; justify-content:center; width:32px; height:32px; border-radius:8px; border:1px solid #e4eaf2; background:#fff; color:#4a5e78; cursor:pointer; font-size:13px; }
.btn-icon:hover { background:#f4f7fb; }
.btn-icon--red { color:#dc2626; }
.btn-icon--red:hover { background:#fee2e2; border-color:#fca5a5; }
.btn-danger  { display:inline-flex; align-items:center; gap:6px; background:#e53e3e; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }

.filter-bar { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:16px; }
.filter-input  { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; min-width:200px; font-family:inherit; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; background:#fff; cursor:pointer; font-family:inherit; }

.table-wrap { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table th { padding:11px 14px; background:#f4f7fb; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; text-align:left; border-bottom:1px solid #e4eaf2; }
.data-table td { padding:12px 14px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.data-table tr:last-child td { border-bottom:none; }
.td-bold { font-weight:600; }
.td-muted { color:#7a90b0; }
.row--inactive td { opacity:.55; }

.threshold-badge { background:#f1f5f9; color:#64748b; font-size:11px; font-weight:700; padding:3px 8px; border-radius:20px; }
.reason-text { font-size:12px; color:#4a5e78; max-width:200px; display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.status-dot  { display:inline-block; width:7px; height:7px; border-radius:50%; margin-right:5px; }
.status-dot--green { background:#22c55e; }
.status-dot--red   { background:#ef4444; }

.pagination-bar { display:flex; align-items:center; gap:10px; padding:12px 16px; border-top:1px solid #f0f4f8; }
.pg-btn  { padding:6px 14px; border:1px solid #e4eaf2; border-radius:8px; background:#fff; font-size:13px; cursor:pointer; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-info { font-size:13px; color:#7a90b0; flex:1; text-align:center; }

.empty-state { text-align:center; padding:60px 20px; }
.empty-icon  { font-size:48px; color:#dde5f0; margin-bottom:16px; }
.empty-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
.empty-sub   { font-size:13px; color:#7a90b0; margin:0 0 20px; }

.screen-result { display:flex; align-items:flex-start; gap:12px; padding:14px; border-radius:10px; margin-top:18px; }
.screen-result--match { background:#fff5f5; border:1px solid #fca5a5; }
.screen-result--clear { background:#f0fdf4; border:1px solid #86efac; }
.screen-result-icon { font-size:22px; margin-top:1px; }
.screen-result--match .screen-result-icon { color:#dc2626; }
.screen-result--clear .screen-result-icon { color:#16a34a; }
.screen-result-title { font-size:14px; font-weight:700; color:#1a2b45; }
.screen-result-sub   { font-size:12px; color:#7a90b0; margin-top:3px; }

.modal-backdrop { position:fixed; inset:0; background:rgba(10,20,40,.45); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
.modal-box { background:#fff; border-radius:16px; width:100%; max-width:500px; max-height:90vh; overflow-y:auto; display:flex; flex-direction:column; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm { max-width:420px; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 22px 14px; border-bottom:1px solid #f0f4f8; }
.modal-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close  { background:none; border:none; font-size:16px; color:#7a90b0; cursor:pointer; padding:4px; }
.modal-body   { padding:22px; flex:1; overflow-y:auto; }
.modal-footer { padding:14px 22px; border-top:1px solid #f0f4f8; display:flex; justify-content:flex-end; gap:10px; }

.form-row  { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.form-hint  { font-size:11px; color:#aab8cc; margin-top:2px; }
.req { color:#e53e3e; }
.two-col { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.range-input { width:100%; accent-color:#004AAD; }
.toggle-row  { display:flex; align-items:center; gap:10px; }
.toggle-name { font-size:13px; font-weight:600; color:#1a2b45; }
.toggle-switch { position:relative; display:inline-block; width:38px; height:22px; flex-shrink:0; }
.toggle-switch input { opacity:0; width:0; height:0; }
.toggle-track  { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:22px; transition:.2s; }
.toggle-track::before { position:absolute; content:''; height:16px; width:16px; left:3px; top:3px; background:#fff; border-radius:50%; transition:.2s; }
.toggle-switch input:checked + .toggle-track { background:#004AAD; }
.toggle-switch input:checked + .toggle-track::before { transform:translateX(16px); }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
</style>
